import { Trans, useLingui } from "@lingui/react/macro";
import type { Bot, ModelCatalogEntry } from "@rakazo/contracts";
import { BotAvatar, NativeSelect, NativeSelectOption, Switch, Textarea } from "@rakazo/ui-web";
import { useQuery } from "@tanstack/react-query";
import { Suspense, useId, useState } from "react";
import { ErrorBoundary, SectionLoadFailed } from "../components/ErrorBoundary";
import { lazyComponent } from "../lib/preload-recovery";
import { useSpaceQueries } from "../lib/query";
import { errorText } from "../lib/user-error";
import { copyOf } from "./copy-capabilities";
import { CopyRoutines } from "./copy-routines";
import { CopySkills } from "./copy-skills";
import { useDrokCopies } from "./use-drok-copies";

const ScratchpadSection = lazyComponent(() =>
  import("../pages/ScratchpadSection").then((module) => module.ScratchpadSection),
);
const KnowledgeSection = lazyComponent(() =>
  import("../pages/KnowledgeSection").then((module) => module.KnowledgeSection),
);

const NO_CATALOG: ModelCatalogEntry[] = [];

/** The two settings an employee may change on their own copy; the server accepts nothing else. */
export type EmployeeVoicePatch = { voiceId?: string | null; autoSpeak?: boolean };

/** The model name to show: the catalog label when known, the raw id otherwise, or null for the default. */
export function modelLabel(bot: Bot, catalog: readonly ModelCatalogEntry[]): string | null {
  if (!bot.modelId) return null;
  const entry = catalog.find(
    (item) =>
      item.id === bot.modelId && (!bot.modelProvider || item.provider === bot.modelProvider),
  );
  return entry?.label ?? bot.modelId;
}

function ReadOnlyBlock({ label, children }: { label: string; children: string }) {
  return (
    <section className="mt-6">
      <h3 className="mb-2 text-xs font-medium text-muted-foreground">{label}</h3>
      <p
        className="whitespace-pre-wrap break-words rounded-xl bg-muted/50 p-4 text-sm text-foreground"
        dir="auto"
      >
        {children}
      </p>
    </section>
  );
}

/**
 * The agent panel an employee sees: name, description, model and instructions read-only, the
 * to-do list, how replies are read aloud, and Clear conversation. On a copy IT pushed it also shows
 * the template version, the routines (pause or resume, run history), the template skills and editable
 * memory. Everything that changes the agent's configuration is left out; the server refuses those anyway.
 */
export function EmployeeAgentPanel({
  bot,
  onSaveVoice,
  onClear,
}: {
  bot: Bot;
  onSaveVoice: (patch: EmployeeVoicePatch) => Promise<void>;
  onClear: () => void;
}) {
  const { t } = useLingui();
  const ids = useId();
  const queries = useSpaceQueries();
  const catalog = useQuery(queries.models.list.queryOptions()).data ?? NO_CATALOG;
  const voices = useQuery(queries.voice.voices.queryOptions({ input: {} })).data;
  const [autoSpeak, setAutoSpeak] = useState(bot.autoSpeak);
  const [voiceId, setVoiceId] = useState(bot.voiceId ?? "");
  const [error, setError] = useState<string | null>(null);
  const model = modelLabel(bot, catalog);
  // While the copies load (or fail to), the panel stays the locked version: memory read-only and no routines.
  const copy = copyOf(useDrokCopies().copies, bot.id);
  const templateName = copy?.templateName ?? "";
  const version = copy?.version ?? 0;

  async function save(patch: EmployeeVoicePatch, undo: () => void) {
    setError(null);
    try {
      await onSaveVoice(patch);
    } catch (err) {
      undo();
      setError(errorText(err, t`Could not save`));
    }
  }

  return (
    <div data-testid="employee-agent-panel">
      <div className="flex flex-col items-center pb-6 pt-2 text-center">
        <BotAvatar
          avatarSet={bot.avatarSet}
          color={bot.color}
          identity={bot.id}
          size={76}
          status={bot.status}
        />
        <h2 className="mt-4 break-words text-xl font-semibold" dir="auto">
          {bot.name}
        </h2>
        {bot.title ? (
          <p className="mt-1 text-sm text-muted-foreground" dir="auto">
            {bot.title}
          </p>
        ) : null}
      </div>
      {copy ? (
        <p
          className="rounded-xl bg-muted/50 px-4 py-2.5 text-center text-[13px] text-muted-foreground"
          data-testid="managed-by-it"
        >
          <Trans>
            Managed by IT: {templateName}, version {version}
          </Trans>
        </p>
      ) : null}
      {bot.description ? (
        <ReadOnlyBlock label={t`Description`}>{bot.description}</ReadOnlyBlock>
      ) : null}
      <ReadOnlyBlock label={t`Model`}>{model ?? t`Default`}</ReadOnlyBlock>
      <section className="mt-6">
        <label
          htmlFor={`${ids}-instructions`}
          className="mb-2 block text-xs font-medium text-muted-foreground"
        >
          <Trans>Instructions</Trans>
        </label>
        <Textarea
          id={`${ids}-instructions`}
          readOnly
          value={bot.instructions}
          className="min-h-20 resize-none field-sizing-content rounded-xl border-transparent bg-muted/50 p-4 font-normal text-foreground shadow-none"
          dir="auto"
        />
      </section>
      {copy ? <CopyRoutines botId={bot.id} /> : null}
      {copy ? <CopySkills skills={copy.skills} /> : null}
      <ErrorBoundary fallback={<SectionLoadFailed />}>
        <Suspense fallback={null}>
          <ScratchpadSection botId={bot.id} />
        </Suspense>
      </ErrorBoundary>
      <ErrorBoundary fallback={<SectionLoadFailed />}>
        <Suspense fallback={null}>
          <KnowledgeSection botId={bot.id} readOnly={!copy} showEmpty={Boolean(copy)} />
        </Suspense>
      </ErrorBoundary>
      <section className="mt-6">
        <h3 className="mb-2 text-xs font-medium text-muted-foreground">
          <Trans>Voice</Trans>
        </h3>
        <div className="space-y-3 rounded-xl bg-muted/50 p-4">
          <label
            htmlFor={`${ids}-auto-speak`}
            className="flex min-h-11 cursor-pointer items-center justify-between gap-3 text-sm text-foreground"
          >
            <Trans>Read replies aloud</Trans>
            <Switch
              id={`${ids}-auto-speak`}
              checked={autoSpeak}
              onCheckedChange={(checked) => {
                setAutoSpeak(checked);
                void save({ autoSpeak: checked }, () => setAutoSpeak(!checked));
              }}
            />
          </label>
          {voices?.length ? (
            <label
              htmlFor={`${ids}-voice`}
              className="flex min-h-11 items-center justify-between gap-3 text-sm text-foreground"
            >
              <Trans>Voice</Trans>
              <NativeSelect
                id={`${ids}-voice`}
                className="max-w-2/3"
                value={voiceId}
                onChange={(event) => {
                  const previous = voiceId;
                  const next = event.target.value;
                  setVoiceId(next);
                  void save({ voiceId: next || null }, () => setVoiceId(previous));
                }}
              >
                <NativeSelectOption value="">{t`Account default`}</NativeSelectOption>
                {voices.map((voice) => (
                  <NativeSelectOption key={voice.id} value={voice.id}>
                    {voice.label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </label>
          ) : null}
        </div>
      </section>
      <div className="mt-6 overflow-hidden rounded-xl bg-muted/50 px-4">
        <button
          type="button"
          className="min-h-12 w-full text-start text-sm text-destructive"
          onClick={onClear}
        >
          <Trans>Clear conversation</Trans>
        </button>
      </div>
      {error ? <p className="mt-2 text-[13px] text-destructive">{error}</p> : null}
    </div>
  );
}
